import { useInfiniteQuery } from '@tanstack/react-query';
import { TPO_GEN_DIM_OPTIONS_QUERY_KEY } from '../utils/tpoGenQueryKeys';
import { tpoGenUrls } from 'config/urls';
import axios from 'axios';
import { type DimensionFilterInclusion, type SelectOption } from '../types/filters';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 400;

const hasValue = (v: unknown): v is string | string[] =>
    Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.length > 0;

const fetchDimOptions = async (
    dimension: string,
    attributeType: string,
    filters?: Record<string, unknown>,
    selectedDimension?: SelectedDimensionType,
    signal?: AbortSignal,
    offset?: number,
    searchString?: string,
) => {
    const token = localStorage.getItem('accessToken');

    const filtersObj = {
        [(selectedDimension?.dimension as string) || 'dim1']:
            selectedDimension && hasValue(selectedDimension.value)
                ? {
                      inclusion: [
                          {
                              attributeType: selectedDimension.attributeType,
                              attributeDesc: selectedDimension.value,
                              attributeExId: selectedDimension.value,
                              dimension: selectedDimension.dimension,
                          },
                      ],
                      exclusion: [],
                  }
                : {},
        dim2: {},
    };
    const response = await axios(tpoGenUrls.getDimOptions, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: {
            attribute_type: attributeType,
            limit: 50,
            offset: offset || 0,
            search_string: searchString ?? '',
            dimension,
            filters: filtersObj,
            feature_name: 'tpo_gen',
            sub_feature_name: 'plan_create',
            action_type: 'default',
        },
        signal: signal,
    });
    return response?.data;
};

interface SelectedDimensionType {
    value: string | string[];
    dimension: string;
    attributeType: string;
}
const useDimOptionsData = ({
    dimension,
    attributeType,
    filters,
    selectedDimension,
    enabled,
    searchString = '',
}: {
    dimension: string;
    attributeType: string;
    filters?: Record<'dim1' | 'dim2', { inclusion: DimensionFilterInclusion[]; exclusion: DimensionFilterInclusion[] }>;
    selectedDimension?: SelectedDimensionType;
    enabled: boolean;
    searchString?: string;
}) => {
    const [debouncedSearchString, setDebouncedSearchString] = useState(searchString);

    const debouncedSetSearchString = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearchString(value);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetSearchString(searchString);
        return () => {
            debouncedSetSearchString.cancel();
        };
    }, [searchString, debouncedSetSearchString]);

    const { data, isPending, isFetching, isFetchingNextPage, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: [
            TPO_GEN_DIM_OPTIONS_QUERY_KEY,
            dimension,
            attributeType,
            selectedDimension,
            debouncedSearchString,
            filters,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchDimOptions(
                dimension,
                attributeType,
                filters,
                selectedDimension,
                signal,
                pageParam,
                debouncedSearchString,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            return lastPage?.total_count > lastPageParam ? lastPageParam + 50 : undefined;
        },
    });

    const options = useMemo<SelectOption[]>(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => page.data.map((item: any) => ({ label: item.label, value: item.label })));
    }, [data]);

    return {
        options,
        isPending,
        isFetching,
        isFetchingNextPage,
        isError,
        hasNextPage,
        fetchNextPage,
    };
};

export default useDimOptionsData;
